/**
 * Tracks: footprints and blood on the ground — the one weather mechanic a
 * player can READ. Tracks are WORLD STATE and so the server's: it keeps the
 * ledger (`server/raid/tracks.ts`), decides when a stride has gone by, and
 * tells each client about nearby marks in the snapshot; the client only DRAWS
 * them (`client/render/tracks.ts`). Both sides read the numbers from here. Pure
 * data and functions.
 */

import type { Weather } from "./env.js";

/**
 * What kind of mark. The two feet are separate kinds because the client mirrors
 * them and the server alternates them — what makes a line of prints read as a
 * walk.
 */
export type TrackKind = "footL" | "footR" | "blood";

/** Wire/iteration order. Append-only. */
export const TRACK_KINDS: readonly TrackKind[] = ["footL", "footR", "blood"];

/**
 * Metres of travel between one print and the next — one STEP, not the 2.1 m
 * stride the footstep audio triggers on; a print every 75 cm is what a walk
 * leaves.
 */
export const TRACK_STEP_M = 0.75;

/**
 * How far each foot sits off the line of travel, metres — half a 25 cm stance.
 */
export const TRACK_FOOT_OFFSET_M = 0.12;

/**
 * Below this horizontal speed a body is standing: no prints, but blood
 * (`BLOOD_STILL_SEC`). The same idle threshold `animFor` uses.
 */
export const TRACK_MIN_SPEED_MPS = 0.2;

/**
 * A bleeding body that is NOT moving still drips, one drop every this many
 * seconds — a spreading stain says "he is still here".
 */
export const BLOOD_STILL_SEC = 2.5;

/**
 * Drops laid around a body the moment it dies — a pool, which makes it read as
 * a corpse from the doorway.
 */
export const BLOOD_DEATH_DROPS = 3;

/**
 * Whether FOOTPRINTS are laid in this weather: snow and rain (mud holds a
 * boot), not clear ground or fog. Blood is left in every weather.
 */
export function footprintsIn(weather: Weather): boolean {
  return weather === "snow" || weather === "rain";
}

/**
 * How long one mark stays on the ground, seconds: snow prints 120 (long enough
 * to follow a man across the village), mud prints 60 (rain fills a print),
 * blood 120. The client fades over the last part; the server forgets at exactly
 * this age.
 */
export function trackTtlSec(kind: TrackKind, weather: Weather): number {
  if (kind === "blood") return 120;
  return weather === "rain" ? 60 : 120;
}

/**
 * Horizontal radius, metres, inside which a client is told about a mark. A 30
 * cm smudge is a fraction of a pixel past eighty metres, and this keeps the
 * feature off the bandwidth bill.
 */
export const TRACK_SEND_RANGE_M = 80;

/**
 * The most marks one snapshot carries to one client — a fresh join beside
 * trampled snow is spread over a few snapshots.
 */
export const TRACK_MAX_PER_SNAPSHOT = 48;

/** Wire guard for a `TrackKind`. */
export function isTrackKind(x: unknown): x is TrackKind {
  return (TRACK_KINDS as readonly unknown[]).includes(x);
}
